import { useAccountContext } from '@ovotech/ui-tools';
import { useSelector } from 'react-redux';

import { useContractsQuery } from '../api';
import { State } from '../types/State';
import { Loadable } from '../types/utils';
import { useFeature } from '../utils/optimizely';
import { Contract } from '../api/contracts/types';

export type AccountBillingType = 'PAYM' | 'PAYG' | 'PAYX';

export type AccountBillingTypeResult = Loadable<{ type: AccountBillingType }>;

const useAccountData = () => {
  const { accountId } = useAccountContext();
  const accounts = useSelector((state: State) => state.app.accounts);

  const selectedAccountId = useSelector(
    (state: State) => state.user.selectedAccountId,
  );
  const id = accountId ? accountId : selectedAccountId;

  const selectedAccount = accounts.find(
    a => String(a.accountId) === String(id),
  );

  return { id, selectedAccount, isLoading: accounts.length === 0 };
};

const getBillingTypeFromContracts = (
  contracts: Contract[],
): AccountBillingType => {
  // Classifies the true billing type. The mixed (PAYX) result is remapped to
  // PAYG at the call site until the PAYX experience exists — see the caller.
  const isPrepayment = (c: Contract) =>
    c.details.latestTariff.paymentMechanism === 'PAYG';

  // No contracts means we can't infer PAYG (`[].every()` is `true`), so default
  // to PAYM rather than misclassifying the account as prepayment.
  if (contracts.length === 0) return 'PAYM';
  if (contracts.every(isPrepayment)) return 'PAYG';
  if (contracts.some(isPrepayment)) return 'PAYX';
  return 'PAYM';
};

export const useAccountBillingType = (): AccountBillingTypeResult => {
  const [isBillingTypeCheckEnabled] = useFeature(
    'contract_api_v2_billing_type',
  );

  const {
    id,
    selectedAccount,
    isLoading: isAccountsLoading,
  } = useAccountData();

  const {
    data: contractsResponse,
    isLoading: isContractsLoading,
    isError: isContractsError,
    error,
  } = useContractsQuery(id, {
    enabled: !!id && isBillingTypeCheckEnabled,
  });

  if (isBillingTypeCheckEnabled) {
    // No selected account yet, so the contracts query is disabled (see
    // `enabled` above) and we can't determine a contract-based type.
    if (!id) {
      // Still bootstrapping accounts: this is the initial render, so report
      // loading rather than flashing a default that would flip a moment later.
      if (isAccountsLoading) {
        return {
          status: 'loading',
          isError: false,
          isLoading: true,
        };
      }
      // Accounts have loaded but none is selected (multi-account, pre-selection).
      // Default to PAYM (non-PAYG): this must be a success, not loading or an
      // error, or the account picker — gated on this hook in `Wrapper` — could
      // never render. This lives inside the contracts branch so it survives
      // removal of the flag and the legacy fallback below.
      return {
        status: 'success',
        isError: false,
        isLoading: false,
        data: { type: 'PAYM' },
      };
    }

    if (isContractsLoading) {
      return {
        status: 'loading',
        isError: false,
        isLoading: true,
      };
    }

    if (isContractsError || !contractsResponse) {
      return {
        status: 'error',
        isError: true,
        isLoading: false,
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }

    const activeContracts = contractsResponse.data.filter(
      c => c.details.status.active,
    );
    const billingType = getBillingTypeFromContracts(activeContracts);

    return {
      status: 'success',
      isError: false,
      isLoading: false,
      data: {
        type: billingType,
      },
    };
  }

  /**
   * Legacy fallback: Reads the billing type from Redux, which is populated once
   * by `WrapperWithData` after the bootstrap query resolves. Using Redux (rather
   * than re-reading the Apollo bootstrap cache via `useBootstrapQuery`) keeps the
   * result stable across remounts — important for nav guards like `PaymOnly` that
   * would otherwise flash on every route change while Apollo's first render settles.
   *
   * Note: This legacy logic relies on `WrapperWithData`'s meter-mode-based PAYG check
   * and will be fully replaced by the contract API logic above once the feature flag
   * is fully rolled out.
   */
  if (isAccountsLoading) {
    return {
      status: 'loading',
      isError: false,
      isLoading: true,
    };
  }

  return {
    status: 'success',
    isError: false,
    isLoading: false,
    data: {
      type: selectedAccount?.isPayg ? 'PAYG' : 'PAYM',
    },
  };
};
